import {
  ArrayFieldModel,
  CheckboxFieldModel,
  DateFieldModel,
  FieldType,
  FileFieldModel,
  FixedFileFieldModel,
  FixedPictureFieldModel,
  FixedTextFieldModel,
  FormField,
  FormModelValidationConfigModel,
  FormModelValidationMode,
  HeaderPictureFieldModel,
  LocalFile,
  PictureFieldModel,
  RadioFieldModel,
  SectionFieldModel,
  SelectEditableFieldModel,
  SelectFieldModel,
  SignatureFieldModel,
  TextFieldModel,
  TitleFieldModel,
} from '@types'

import { FormFieldArray } from './components/FormFieldArray/FormFieldArray.component'
import { FormFieldCheckbox } from './components/FormFieldCheckbox'
import { FormFieldConfigValidate } from './components/FormFieldConfigValidate'
import { FormFieldDate } from './components/FormFieldDate'
import { FormFieldFile } from './components/FormFieldFile'
import { FormFieldFixedFile } from './components/FormFieldFixedFile'
import { FormFieldFixedPicture } from './components/FormFieldFixedPicture'
import { FormFieldFixedText } from './components/FormFieldFixedText'
import { FormFieldHeaderPicture } from './components/FormFieldHeaderPicture'
import { FormFieldPicture } from './components/FormFieldPicture'
import { FormFieldRadio } from './components/FormFieldRadio'
import { FormFieldSection } from './components/FormFieldSection'
import { FormFieldSelect } from './components/FormFieldSelect'
import { FormFieldSelectEditable } from './components/FormFieldSelectEditable'
import { FormFieldSignature } from './components/FormFieldSignature'
import { FormFieldText } from './components/FormFieldText'
import { FormFieldTitle } from './components/FormFieldTitle'
import { FormFieldType, MenuFormModels } from './components/MenuFormModels'
import { TitleInput } from './components/TitleInput'

import { useFormBuilder } from '../../hooks/useFormBuilder.hooks'

import { Container, Field, FieldList } from './FormBuilder.styled'

interface FormBuilderProps {
  fields: FormField[]
  title: string
  validationConfig?: FormModelValidationConfigModel
  projectId: string
  setFields: React.Dispatch<React.SetStateAction<FormField[]>>
  setTitle: React.Dispatch<React.SetStateAction<string>>
  setLocalFiles: React.Dispatch<React.SetStateAction<Record<string, LocalFile[]>>>
  setValidationConfig: React.Dispatch<React.SetStateAction<FormModelValidationConfigModel | undefined>>
}

export const FormBuilder: React.FC<FormBuilderProps> = ({
  title,
  fields,
  validationConfig,
  projectId,
  setLocalFiles,
  setFields,
  setTitle,
  setValidationConfig,
}: FormBuilderProps) => {
  const {
    availableSections,
    filteredFields,
    fieldsToRemoveInMenu,
    handleLocalFilesChange,
    handleFieldAdd,
    handleFieldArrayAdd,
    handleFieldArrayRemove,
    handleDeleteOptionInArray,
    handleDeleteOption,
    handleFieldDelete,
    handleFieldMove,
    handleFieldDuplicate,
    handleFieldChange,
    handleValidationConfigChange,
    handleValidationConfigDelete,
  } = useFormBuilder({
    fields,
    validationConfig,
    setFields,
    setLocalFiles,
    setValidationConfig,
  })

  const renderField = (field: FormField, index: number) => {
    const requiredProps = {
      hideRequired: validationConfig?.mode === FormModelValidationMode.OnChange,
    }

    const commonProps = {
      canUp: index > 0,
      canDown: index < fields.length - 1,
      onChange: (updates: Partial<FormField>) => handleFieldChange(field.id, updates),
      onDelete: () => handleFieldDelete(field.id),
      onUp: () => handleFieldMove(field.id, 'up'),
      onDown: () => handleFieldMove(field.id, 'down'),
      onDuplicate:
        field.type !== FieldType.FixedFile && field.type !== FieldType.FixedPicture
          ? () => handleFieldDuplicate(field.id)
          : undefined,
    }

    const optionProps = {
      onDeleteOption: (fieldId: string, optionId: string) => handleDeleteOption(fieldId, optionId),
      projectId: projectId,
    }

    const fileProps = {
      ...commonProps,
      onLocalFilesChange: (files: LocalFile[]) => handleLocalFilesChange(field.id, files),
    }

    const arrayProps = {
      ...commonProps,
      ...requiredProps,
      sections: availableSections,
      projectId: projectId,
      onAddField: (fieldId: string, type: FieldType) => handleFieldArrayAdd(fieldId, type),
      onRemoveField: (arrayFieldId: string, fieldId: string) => handleFieldArrayRemove(arrayFieldId, fieldId),
      onDeleteOption: (arrayFieldId: string, fieldId: string, optionId: string) =>
        handleDeleteOptionInArray(arrayFieldId, fieldId, optionId),
    }

    const titleProps = {
      onChange: (updates: Partial<FormField>) => handleFieldChange(field.id, updates),
      onDelete: () => handleFieldDelete(field.id),
    }

    const headerPictureProps = {
      onChange: (updates: Partial<FormField>) => handleFieldChange(field.id, updates),
      onLocalFilesChange: (files: LocalFile[]) => handleLocalFilesChange(field.id, files),
      onDelete: () => handleFieldDelete(field.id),
    }

    switch (field.type) {
      case FieldType.HeaderPicture:
        return <FormFieldHeaderPicture field={field as HeaderPictureFieldModel} {...headerPictureProps} />
      case FieldType.Title:
        return <FormFieldTitle field={field as TitleFieldModel} {...titleProps} />
      case FieldType.Section:
        return <FormFieldSection field={field as SectionFieldModel} {...commonProps} />
      case FieldType.Array:
        return <FormFieldArray field={field as ArrayFieldModel} {...arrayProps} />
      case FieldType.Text:
        return <FormFieldText field={field as TextFieldModel} {...commonProps} {...requiredProps} />
      case FieldType.File:
        return <FormFieldFile field={field as FileFieldModel} {...commonProps} {...requiredProps} />
      case FieldType.Picture:
        return <FormFieldPicture field={field as PictureFieldModel} {...commonProps} {...requiredProps} />
      case FieldType.Date:
        return <FormFieldDate field={field as DateFieldModel} {...commonProps} {...requiredProps} />
      case FieldType.Signature:
        return <FormFieldSignature field={field as SignatureFieldModel} {...commonProps} {...requiredProps} />
      case FieldType.FixedText:
        return <FormFieldFixedText field={field as FixedTextFieldModel} {...commonProps} />
      case FieldType.FixedFile:
        return <FormFieldFixedFile field={field as FixedFileFieldModel} {...fileProps} />
      case FieldType.FixedPicture:
        return <FormFieldFixedPicture field={field as FixedPictureFieldModel} {...fileProps} />
      case FieldType.Checkbox:
        return (
          <FormFieldCheckbox field={field as CheckboxFieldModel} {...optionProps} {...commonProps} {...requiredProps} />
        )
      case FieldType.Radio:
        return (
          <FormFieldRadio
            field={field as RadioFieldModel}
            sections={availableSections}
            {...optionProps}
            {...commonProps}
            {...requiredProps}
          />
        )
      case FieldType.Select:
        return (
          <FormFieldSelect
            field={field as SelectFieldModel}
            sections={availableSections}
            {...optionProps}
            {...commonProps}
            {...requiredProps}
          />
        )
      case FieldType.SelectEditable:
        return (
          <FormFieldSelectEditable
            field={field as SelectEditableFieldModel}
            sections={availableSections}
            {...optionProps}
            {...commonProps}
            {...requiredProps}
          />
        )
      default:
        return null
    }
  }

  return (
    <Container>
      <TitleInput onChange={setTitle} value={title} />

      <FieldList>
        {filteredFields.map((field, index) => (
          <Field key={field.id}>{renderField(field, index)}</Field>
        ))}
      </FieldList>

      {validationConfig?.mode === FormModelValidationMode.Submit && (
        <FormFieldConfigValidate
          onChange={handleValidationConfigChange}
          onDelete={handleValidationConfigDelete}
          allowAdminEdit={validationConfig.allowAdminEdit}
        />
      )}
      <MenuFormModels onSelect={handleFieldAdd} fieldsToRemove={fieldsToRemoveInMenu as FormFieldType[]} />
    </Container>
  )
}
